import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";

const LOGO = "/logo-blue.png";

const NAV_ITEMS = [
  { label: "About", href: "#about" },
  { label: "Plans", href: "#plans" },
  { label: "Benefits", href: "#benefits" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(11,32,59,0.08)]" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO} alt="Legacy Security Life" className="h-12 w-12 rounded-md object-cover" />
            <div className="hidden sm:block leading-tight">
              <p className="font-serif text-lg text-navy">Legacy Security Life</p>
              <p className="text-[11px] tracking-[0.18em] uppercase text-gold-deep">Final Expense Solutions</p>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
            {NAV_ITEMS.map((it) => (
              <a
                key={it.href}
                href={it.href}
                data-testid={`nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[15px] font-medium text-navy/80 hover:text-navy transition-colors"
              >
                {it.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:8333095433"
              data-testid="header-phone-link"
              className="hidden lg:inline-flex items-center gap-2 text-navy text-[15px] font-semibold"
            >
              <Phone className="h-4 w-4 text-gold-deep" />
              833-309-LIFE
            </a>
            <Button
              data-testid="header-quote-cta"
              onClick={() => document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#C5A059] hover:bg-[#B08A3E] text-[#0B203B] font-semibold h-11 px-5 rounded-md"
            >
              Get Free Quote
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-5 border-t border-border" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 pt-3">
              {NAV_ITEMS.map((it) => (
                <a
                  key={it.href}
                  href={it.href}
                  onClick={() => setOpen(false)}
                  className="px-2 py-3 text-navy font-medium hover:bg-muted rounded-md"
                  data-testid={`mobile-nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {it.label}
                </a>
              ))}
              <Button
                onClick={() => {
                  setOpen(false);
                  document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-2 bg-[#C5A059] hover:bg-[#B08A3E] text-[#0B203B] font-semibold h-12"
                data-testid="mobile-quote-cta"
              >
                Get Free Quote
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
